export type ViewerSession = {
  nonce: string;
  expires_at: number;
  expires_in?: number;
  refresh_at_ms?: number | null;
  viewer?: Record<string, unknown>;
};

export type ViewerHandshakeMode = 'session' | 'unavailable' | 'silent';

export type ViewerHandshakeStartOptions = {
  mode: ViewerHandshakeMode;
  unavailableReason?: string | null;
};

export type ViewerHandshakeCallbacks = {
  fetchSession: (signal: AbortSignal) => Promise<ViewerSession | null>;
  postSession: (session: ViewerSession) => void;
  postUnavailable: (reason: string) => void;
  postRevoked: (reason: string) => void;
  postHello: () => void;
  onError?: (error: unknown) => void;
};

export type ViewerHandshake = {
  updateCallbacks: (callbacks: ViewerHandshakeCallbacks) => void;
  start: (options: ViewerHandshakeStartOptions) => void;
  stop: () => void;
  reset: () => void;
  handleReady: () => void;
  handleRefreshRequest: () => void;
  pulse: () => void;
  isReady: () => boolean;
};

export const VIEWER_HANDSHAKE_GRACE_MS = 1_000;
export const VIEWER_HANDSHAKE_SLOW_RETRY_MS = 15_000;

const FAST_RETRY_DELAYS_MS = [250, 1_000] as const;
const SESSION_UNAVAILABLE_REASON = 'session_unavailable';
const SESSION_REVOKED_REASON = 'refresh_failed';

type Acquisition = 'idle' | 'acquiring' | 'available' | 'unavailable' | 'revoked';
type FetchPurpose = 'acquire' | 'refresh';

class ViewerHandshakeController implements ViewerHandshake {
  private callbacks: ViewerHandshakeCallbacks;
  private active = false;
  private mode: ViewerHandshakeMode = 'silent';
  private unavailableReason: string | null = null;
  private ready = false;
  private graceExpired = false;
  private acquisition: Acquisition = 'idle';
  private session: ViewerSession | null = null;
  private sentNonce: string | null = null;
  private sentUnavailableReason: string | null = null;
  private sentRevokedReason: string | null = null;
  private slowRetryPending = false;
  private fetchController: AbortController | null = null;
  private graceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(callbacks: ViewerHandshakeCallbacks) {
    this.callbacks = callbacks;
  }

  updateCallbacks(callbacks: ViewerHandshakeCallbacks): void {
    this.callbacks = callbacks;
  }

  start(options: ViewerHandshakeStartOptions): void {
    if (this.active) this.stop();

    const modeChanged = this.mode !== options.mode || this.unavailableReason !== (options.unavailableReason || null);
    this.mode = options.mode;
    this.unavailableReason = options.unavailableReason || null;
    this.active = true;
    this.graceExpired = this.ready;

    if (modeChanged) this.resetAcquisitionForMode();

    if (this.mode !== 'silent' && !this.ready) {
      this.callbacks.postHello();
      this.scheduleGrace();
    }

    if (this.mode === 'session') {
      if (this.session) {
        this.attemptDelivery();
        this.scheduleSessionRefresh();
      } else if (this.slowRetryPending) {
        this.runSlowRetry();
      } else if (this.acquisition === 'idle') {
        this.runFetch('acquire', 0);
      } else {
        this.attemptDelivery();
      }
    } else {
      this.attemptDelivery();
    }
  }

  stop(): void {
    this.active = false;
    this.clearTimers();
    this.cancelFetch();
    // Acquiring never has a stored session (a refresh fetch keeps 'available'), so a
    // restart refetches from 'idle'.
    if (this.acquisition === 'acquiring') this.acquisition = 'idle';
  }

  reset(): void {
    this.stop();
    this.mode = 'silent';
    this.unavailableReason = null;
    this.ready = false;
    this.graceExpired = false;
    this.acquisition = 'idle';
    this.session = null;
    this.sentNonce = null;
    this.sentUnavailableReason = null;
    this.sentRevokedReason = null;
    this.slowRetryPending = false;
  }

  handleReady(): void {
    this.ready = true;
    this.graceExpired = true;
    this.clearGraceTimer();
    // A ready message may name a new child document after same-origin navigation. Re-send the
    // authoritative answer even when its value matches the previous document's answer.
    this.sentNonce = null;
    this.sentUnavailableReason = null;
    this.sentRevokedReason = null;
    this.attemptDelivery();
  }

  handleRefreshRequest(): void {
    if (!this.active || this.mode !== 'session') return;
    if (!this.session) {
      if (this.acquisition === 'idle') this.runFetch('acquire', 0);
      return;
    }

    this.runFetch('refresh', 0);
  }

  pulse(): void {
    if (!this.active || this.mode !== 'session') return;

    if (this.slowRetryPending) {
      this.runSlowRetry();
      return;
    }

    const refreshAt = Number(this.session?.refresh_at_ms);
    if (this.session && Number.isFinite(refreshAt) && Date.now() >= refreshAt) this.runFetch('refresh', 0);
  }

  isReady(): boolean {
    return this.ready;
  }

  private resetAcquisitionForMode(): void {
    this.clearRetryTimer();
    this.clearRefreshTimer();
    this.cancelFetch();
    this.session = null;
    this.sentNonce = null;
    this.sentUnavailableReason = null;
    this.sentRevokedReason = null;
    this.slowRetryPending = false;

    if (this.mode === 'unavailable') this.acquisition = 'unavailable';
    else this.acquisition = 'idle';
  }

  private scheduleGrace(): void {
    this.clearGraceTimer();
    this.graceExpired = false;
    this.graceTimer = setTimeout(() => {
      this.graceTimer = null;
      if (!this.active) return;
      this.graceExpired = true;
      this.attemptDelivery();
    }, VIEWER_HANDSHAKE_GRACE_MS);
  }

  private runFetch(purpose: FetchPurpose, fastAttempt: number): void {
    if (!this.active || this.mode !== 'session' || this.fetchController) return;

    const controller = new AbortController();
    this.fetchController = controller;
    if (purpose === 'acquire') this.acquisition = 'acquiring';

    // Every cancel path (stop/reset) aborts and nulls fetchController, and only one fetch is
    // ever in flight, so controller identity is the complete staleness check.
    this.callbacks.fetchSession(controller.signal).then(
      (session) => {
        if (this.fetchController !== controller) return;
        this.fetchController = null;
        if (!session?.nonce) {
          this.handleFetchFailure(purpose, fastAttempt, null);
          return;
        }

        this.session = session;
        this.acquisition = 'available';
        this.slowRetryPending = false;
        this.sentNonce = null;
        this.sentUnavailableReason = null;
        this.sentRevokedReason = null;
        this.clearRetryTimer();
        this.attemptDelivery();
        this.scheduleSessionRefresh();
      },
      (error) => {
        if (this.fetchController !== controller) return;
        this.fetchController = null;
        this.callbacks.onError?.(error);
        this.handleFetchFailure(purpose, fastAttempt, errorStatus(error));
      },
    );
  }

  private handleFetchFailure(purpose: FetchPurpose, fastAttempt: number, status: number | null): void {
    const transient = isTransientStatus(status);

    if (purpose === 'refresh') {
      if (transient) {
        this.slowRetryPending = true;
        this.scheduleSlowRetry();
      } else {
        this.session = null;
        this.acquisition = 'revoked';
        this.slowRetryPending = false;
        this.sentRevokedReason = null;
        this.attemptDelivery();
      }
      return;
    }

    if (!transient) {
      this.acquisition = 'unavailable';
      this.slowRetryPending = false;
      this.sentUnavailableReason = null;
      this.attemptDelivery();
      return;
    }

    if (status !== 429 && fastAttempt < FAST_RETRY_DELAYS_MS.length) {
      this.scheduleRetry(FAST_RETRY_DELAYS_MS[fastAttempt], () => this.runFetch('acquire', fastAttempt + 1));
      return;
    }

    this.acquisition = 'unavailable';
    this.slowRetryPending = true;
    this.sentUnavailableReason = null;
    this.attemptDelivery();
    this.scheduleSlowRetry();
  }

  private runSlowRetry(): void {
    if (!this.slowRetryPending) return;
    this.clearRetryTimer();
    // A pending slow retry with a stored session can only be a refresh retry; without one it
    // re-acquires with the fast lane already spent.
    this.runFetch(this.session ? 'refresh' : 'acquire', FAST_RETRY_DELAYS_MS.length);
  }

  private scheduleSlowRetry(): void {
    this.scheduleRetry(VIEWER_HANDSHAKE_SLOW_RETRY_MS, () => this.runSlowRetry());
  }

  private scheduleRetry(delay: number, callback: () => void): void {
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.active) callback();
    }, delay);
  }

  private scheduleSessionRefresh(): void {
    this.clearRefreshTimer();
    const refreshAt = Number(this.session?.refresh_at_ms);
    if (!Number.isFinite(refreshAt) || refreshAt <= 0) return;

    this.refreshTimer = setTimeout(
      () => {
        this.refreshTimer = null;
        if (this.active) this.runFetch('refresh', 0);
      },
      Math.max(0, refreshAt - Date.now()),
    );
  }

  private attemptDelivery(): void {
    if (!this.active || this.mode === 'silent' || (!this.ready && !this.graceExpired)) return;

    if (this.session) {
      if (this.sentNonce === this.session.nonce) return;
      this.callbacks.postSession(this.session);
      this.sentNonce = this.session.nonce;
      return;
    }

    if (this.acquisition === 'unavailable') {
      // Mode 'unavailable' pins acquisition to 'unavailable' (resetAcquisitionForMode), so the
      // server-provided reason wins there; acquisition failures use the generic reason.
      const reason = (this.mode === 'unavailable' && this.unavailableReason) || SESSION_UNAVAILABLE_REASON;
      if (this.sentUnavailableReason === reason) return;
      this.callbacks.postUnavailable(reason);
      this.sentUnavailableReason = reason;
      return;
    }

    if (this.acquisition === 'revoked') {
      if (this.sentRevokedReason === SESSION_REVOKED_REASON) return;
      this.callbacks.postRevoked(SESSION_REVOKED_REASON);
      this.sentRevokedReason = SESSION_REVOKED_REASON;
    }
  }

  private cancelFetch(): void {
    this.fetchController?.abort();
    this.fetchController = null;
  }

  private clearTimers(): void {
    this.clearGraceTimer();
    this.clearRetryTimer();
    this.clearRefreshTimer();
  }

  private clearGraceTimer(): void {
    if (this.graceTimer !== null) clearTimeout(this.graceTimer);
    this.graceTimer = null;
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }
}

export function createViewerHandshake(callbacks: ViewerHandshakeCallbacks): ViewerHandshake {
  return new ViewerHandshakeController(callbacks);
}

function errorStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('status' in error)) return null;
  const status = Number((error as { status?: unknown }).status);
  return Number.isFinite(status) ? status : null;
}

function isTransientStatus(status: number | null): boolean {
  if (status === null || status === 0 || status === 408 || status === 429) return true;
  return status >= 500;
}
